import { useAppSelector } from '../../../../redux/hooks.ts'
import { selectThemeStyle } from '../../../../features/setting/setting.slice.ts'
import { useFixedBreakpoint } from '../../../../common/hooks/use-is-mobile.ts'
import Row from 'antd/es/grid/row'
import Col from 'antd/es/grid/col'
import { convertPxToNumber } from '../../../../utils/helpers.ts'
import { selectUser } from '../../../../features/user/user.slice.ts'
import { IdeaSummaryBpUsageCard } from './idea-summary-bp-usage.tsx'
import { IdeaSummaryNetExposure } from './idea-summary-net-exposure.tsx'
import { IdeaSummaryDailyPnlCard } from './idea-summary-daily-pnl.tsx'
import { IdeaSummaryStarScore } from './idea-summary-star-score.tsx'
import { useEffect, useRef } from 'react'
import { useTutorial } from '../../../../contexts/use-tutorial.ts'
import {
  useGetBuyingPowerQuery,
  useGetDeploymentDateRangeQuery,
  useGetFullyPnlQuery,
  useGetNetExposureQuery,
  useGetUserScoreQuery,
  useLazyGetBookSnapshotQuery,
} from '../../../../apis/statistics/statistics-api.api.ts'
import {
  getCurrentQuarterRange,
  isHistoricalEndDate,
} from '../../../../utils/time.ts'
import dayjs from 'dayjs'

/**
 * Props for SummaryInfo — accepts external filter data so the component
 * can be used by both Idea List (ideaListInput) and Dashboard (dashboardInput).
 */
export interface SummaryInfoProps {
  /** Selected contributor id. Pass '' when no contributor concept applies. */
  contributor: string
  /** The book id derived from filter selection. */
  bookId: string
  /** Date range tuple [start, end] as ISO strings or null. */
  dateRange: [string | null, string | null]
}

export const SummaryInfo = ({
  contributor,
  bookId: inputBookId,
  dateRange,
}: SummaryInfoProps) => {
  const themeStyle = useAppSelector(selectThemeStyle)
  const screens = useFixedBreakpoint()
  const isMobile = !screens.md
  const userInfo = useAppSelector(selectUser)
  const summaryDataRef = useRef(null)
  const { setRef } = useTutorial()

  // Derive bookId: when no contributor is selected, fall back to user's own bookId
  const bookId = contributor === '' ? userInfo.bookId : inputBookId

  const shouldSkip = !bookId || dateRange.some((d) => d === null)

  // ----- API Hooks -----

  const {
    data: buyingPower,
    isLoading: isBuyingPowerLoading,
    isFetching: isBuyingPowerFetching,
  } = useGetBuyingPowerQuery({ bookId }, { skip: shouldSkip })

  const {
    data: netExposureData,
    isLoading: isNetExposureLoading,
    isFetching: isNetExposureFetching,
  } = useGetNetExposureQuery({ bookId }, { skip: shouldSkip })

  const {
    data: fullyPnlData,
    isLoading: isFullyPnlLoading,
    isFetching: isFullyPnlFetching,
  } = useGetFullyPnlQuery({ bookId }, { skip: shouldSkip })

  const [
    getSnapshotData,
    {
      currentData: snapshotResult,
      isLoading: isSnapshotLoading,
      isFetching: isSnapshotFetching,
    },
  ] = useLazyGetBookSnapshotQuery()

  const {
    data: deploymentData,
    isLoading: isDeploymentLoading,
    isFetching: isDeploymentFetching,
  } = useGetDeploymentDateRangeQuery(
    {
      bookId,
      startDate: dayjs(dateRange[0]).format('YYYY-MM-DD'),
      endDate: dayjs(dateRange[1]).format('YYYY-MM-DD'),
    },
    { skip: shouldSkip }
  )

  const {
    data: userScoreData,
    isLoading: isUserScoreLoading,
    isFetching: isUserScoreFetching,
  } = useGetUserScoreQuery({ bookId }, { skip: shouldSkip })

  const isStarScoreLoading =
    isUserScoreLoading ||
    isUserScoreFetching ||
    isSnapshotLoading ||
    isSnapshotFetching

  // ----- Historical state detection -----

  const isHistorical = isHistoricalEndDate(dateRange[1])

  // Trigger snapshot lazy query when viewing historical date ranges
  useEffect(() => {
    const isNonCurrentQuarter =
      dateRange !== getCurrentQuarterRange() && dayjs(dateRange[1]).isValid()

    if (!isNonCurrentQuarter) return

    // Determine which bookId to use for snapshot based on user role and contributor state
    let targetBookId: string | undefined

    if (userInfo.role !== '') {
      // Internal admins: use the selected book's id
      targetBookId = inputBookId
    } else if (contributor === '') {
      // Regular user with no contributor selected: prefer own bookId
      targetBookId = userInfo.bookId || inputBookId
    } else {
      // Regular user with contributor selected: use the contributor's bookId
      targetBookId = inputBookId
    }

    if (!targetBookId) return

    getSnapshotData({
      bookId: targetBookId,
      date: dayjs(dateRange[1]).format('YYYY-MM-DD'),
    })
  }, [
    getSnapshotData,
    inputBookId,
    dateRange,
    userInfo.bookId,
    contributor,
    userInfo.role,
  ])

  // ----- Composed loading states -----

  const isBuyingPowerSectionLoading =
    isBuyingPowerLoading ||
    isBuyingPowerFetching ||
    isSnapshotFetching ||
    isDeploymentFetching ||
    isDeploymentLoading

  const isNetExposureSectionLoading =
    isNetExposureLoading || isNetExposureFetching || isSnapshotFetching

  const isPnlSectionLoading =
    isFullyPnlLoading || isFullyPnlFetching || isSnapshotFetching

  // ----- Derived data -----

  const snapshotData = snapshotResult?.data
  const netExposure = netExposureData?.data
  const pnlData = fullyPnlData?.data
  const deployment = deploymentData?.data.deployment

  useEffect(() => {
    setRef('summaryData', summaryDataRef)

    return () => setRef('summaryData', null) // Cleanup when unmounting
  }, [setRef])

  const shouldShowSelectContributorMsg = contributor === '' && !userInfo.bookId

  const usedBuyingPower = isHistorical
    ? (snapshotData?.usedBuyingPower ?? buyingPower?.data.usedBuyingPower ?? 0)
    : (buyingPower?.data.usedBuyingPower ?? snapshotData?.usedBuyingPower ?? 0)

  const totalBuyingPower = isHistorical
    ? (snapshotData?.buyingPower ?? buyingPower?.data.buyingPower ?? 0)
    : (buyingPower?.data.buyingPower ?? snapshotData?.buyingPower ?? 0)

  const netExposureOnGMV = isHistorical
    ? (snapshotData?.netExposureOnGMV ?? netExposure?.netExposureOnGMV ?? 0)
    : (netExposure?.netExposureOnGMV ?? snapshotData?.netExposureOnGMV ?? 0)

  const rawNetCategory = netExposure?.netCategory ?? userInfo.region
  const netCategory =
    rawNetCategory === 'Low Net'
      ? 'Greater China - Low Net'
      : rawNetCategory === 'Long Bias'
        ? 'Greater China - Long Bias'
        : rawNetCategory

  const dailyPnl = isHistorical
    ? (snapshotData?.dailyPnl ?? pnlData?.dailyPnl ?? 0)
    : (pnlData?.dailyPnl ?? snapshotData?.dailyPnl ?? 0)

  const wtdPnl = isHistorical
    ? (snapshotData?.wtdPnl ?? pnlData?.wtdPnl ?? 0)
    : (pnlData?.wtdPnl ?? snapshotData?.wtdPnl ?? 0)

  const mtdPnl = isHistorical
    ? (snapshotData?.mtdPnl ?? pnlData?.mtdPnl ?? 0)
    : (pnlData?.mtdPnl ?? snapshotData?.mtdPnl ?? 0)

  const qtdPnl = isHistorical
    ? (snapshotData?.qtdPnl ?? pnlData?.qtdPnl ?? 0)
    : (pnlData?.qtdPnl ?? snapshotData?.qtdPnl ?? 0)

  const ytdPnl = isHistorical
    ? (snapshotData?.ytdPnl ?? pnlData?.ytdPnl ?? 0)
    : (pnlData?.ytdPnl ?? snapshotData?.ytdPnl ?? 0)

  const lmv = isHistorical
    ? (snapshotData?.lmv ?? netExposure?.lmv ?? 0)
    : (netExposure?.lmv ?? snapshotData?.lmv ?? 0)

  const smv = isHistorical
    ? (snapshotData?.smv ?? netExposure?.smv ?? 0)
    : (netExposure?.smv ?? snapshotData?.smv ?? 0)

  const deploymentValue = deployment ?? 0

  const starScore = userScoreData?.data.latestScore
  const previousScore = userScoreData?.data.previousScore

  const starScoreValue = isHistorical
    ? (snapshotData?.latestScore ?? starScore ?? '')
    : (starScore ?? snapshotData?.latestScore ?? '')

  const previousScoreValue = isHistorical
    ? (snapshotData?.previousScore ?? previousScore ?? '')
    : (previousScore ?? snapshotData?.previousScore ?? '')

  return (
    <Row
      ref={summaryDataRef}
      gutter={[
        isMobile ? 0 : convertPxToNumber(themeStyle.marginBase),
        isMobile ? 0 : convertPxToNumber(themeStyle.marginBase),
      ]}
      wrap={true}
    >
      <Col
        xs={24}
        sm={12}
        md={12}
        lg={12}
        xl={6}
        xxl={6}
        style={{ marginBottom: isMobile ? 16 : 0 }}
      >
        <IdeaSummaryBpUsageCard
          shouldShowSelectContributorMsg={shouldShowSelectContributorMsg}
          isLoading={isBuyingPowerSectionLoading}
          usedBuyingPower={usedBuyingPower}
          totalBuyingPower={totalBuyingPower}
          deployment={deploymentValue}
        />
      </Col>
      <Col
        xs={24}
        sm={12}
        md={12}
        lg={12}
        xl={6}
        xxl={6}
        style={{ marginBottom: isMobile ? 16 : 0 }}
      >
        <IdeaSummaryNetExposure
          shouldShowSelectContributorMsg={shouldShowSelectContributorMsg}
          isLoading={isNetExposureSectionLoading}
          netExposureOnGmv={netExposureOnGMV}
          groupName={netCategory}
          lmv={lmv}
          smv={smv}
        />
      </Col>
      <Col
        xs={24}
        sm={12}
        md={12}
        lg={12}
        xl={6}
        xxl={6}
        style={{ marginBottom: isMobile ? 16 : 0 }}
      >
        <IdeaSummaryDailyPnlCard
          shouldShowSelectContributorMsg={shouldShowSelectContributorMsg}
          isLoading={isPnlSectionLoading}
          dailyPnl={dailyPnl}
          totalBp={totalBuyingPower}
          wtd={wtdPnl}
          mtd={mtdPnl}
          qtd={qtdPnl}
          ytd={ytdPnl}
        />
      </Col>
      <Col
        xs={24}
        sm={12}
        md={12}
        lg={12}
        xl={6}
        xxl={6}
        style={{ marginBottom: isMobile ? 16 : 0 }}
      >
        <IdeaSummaryStarScore
          shouldShowSelectContributorMsg={shouldShowSelectContributorMsg}
          isLoading={isStarScoreLoading}
          score={starScoreValue}
          previousScore={previousScoreValue}
        />
      </Col>
    </Row>
  )
}
